import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Button, DatePicker, Form, Input, Modal, notification, Radio, Select, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { coreApi } from "../../api/calls";
import CitiesSelector from "../Cities/CitiesSelector";
import moment from "moment";

const { TextArea } = Input;

const CampaignAddForm = ({ onSuccess }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [form] = Form.useForm();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [periodType, setPeriodType] = useState("quarter");

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      // Calculate start_date and end_date based on period type
      let startDate, endDate;
      
      if (periodType === "quarter") {
        const year = moment().year();
        const quarters = {
          Q1: { from: `${year}-01-01`, to: `${year}-03-31` },
          Q2: { from: `${year}-04-01`, to: `${year}-06-30` },
          Q3: { from: `${year}-07-01`, to: `${year}-09-30` },
          Q4: { from: `${year}-10-01`, to: `${year}-12-31` },
        };
        startDate = quarters[values.quarter].from;
        endDate = quarters[values.quarter].to;
      } else if (periodType === "month") {
        const [year, month] = values.month.split("-");
        startDate = moment(`${year}-${month}-01`).format("YYYY-MM-DD");
        endDate = moment(`${year}-${month}-01`).endOf("month").format("YYYY-MM-DD");
      } else {
        startDate = values.start_date.format("YYYY-MM-DD");
        endDate = values.end_date.format("YYYY-MM-DD");
      }

      const response = await coreApi.post("/campaigns", {
        name: values.name,
        description: values.description,
        is_active: values.is_active,
        is_recurring: values.is_recurring,
        start_date: startDate,
        end_date: endDate,
        period_type: periodType, // quarter, month, или custom
        city_branch_ids: values.city_branches,
      });
      
      notification.success({
        message: t("campaigns.createSuccess"),
      });
      form.resetFields();
      setModalOpen(false);
      setPeriodType("quarter");
      onSuccess();
      history.push(`/campaigns/${response.data.id}/banners`);
    } catch (error) {
      console.error(error);
      notification.error({
        message: error?.response?.data?.message || t("campaigns.createError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="primary"
        size="large"
        icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)}
      >
        {t("campaigns.addCampaign")}
      </Button>

      <Modal
        width={700}
        title={t("campaigns.createNew")}
        open={isModalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setPeriodType("quarter");
        }}
        okText={t("campaigns.create")}
        cancelText={t("common.cancel")}
        okButtonProps={{ loading: isSubmitting }}
        onOk={() => form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            is_recurring: true,
            quarter: "Q1",
          }}
        >
          <Form.Item
            name="name"
            label={t("campaigns.name")}
            rules={[
              { required: true, message: t("campaigns.nameRequired") },
              { min: 3, message: t("campaigns.nameMinLength") },
              { max: 255, message: t("campaigns.nameMaxLength") },
            ]}
          >
            <Input placeholder={t("campaigns.namePlaceholder")} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("campaigns.description")}
            rules={[{ max: 500, message: t("campaigns.descriptionMaxLength") }]}
          >
            <TextArea
              rows={4}
              placeholder={t("campaigns.descriptionPlaceholder")}
              showCount
              maxLength={500}
            />
          </Form.Item>

          {/* Period Type Selection */}
          <Form.Item label={t("campaigns.periodType")}>
            <Radio.Group 
              value={periodType} 
              onChange={(e) => {
                setPeriodType(e.target.value);
                // Reset period-specific fields
                form.setFieldsValue({
                  quarter: undefined,
                  month: undefined,
                  start_date: undefined,
                  end_date: undefined,
                });
              }}
            >
              <Radio value="quarter">{t("campaigns.periodQuarter")}</Radio>
              <Radio value="month">{t("campaigns.periodMonth")}</Radio>
              <Radio value="custom">{t("campaigns.periodCustom")}</Radio>
            </Radio.Group>
          </Form.Item>

          {/* Quarter Selection */}
          {periodType === "quarter" && (
            <Form.Item
              name="quarter"
              label={t("campaigns.selectQuarter")}
              rules={[{ required: true, message: t("campaigns.selectPeriodRequired") }]}
            >
              <Select placeholder={t("campaigns.selectQuarterPlaceholder")}>
                <Select.Option value="Q1">Q1 (01.01 - 31.03)</Select.Option>
                <Select.Option value="Q2">Q2 (01.04 - 30.06)</Select.Option>
                <Select.Option value="Q3">Q3 (01.07 - 30.09)</Select.Option>
                <Select.Option value="Q4">Q4 (01.10 - 31.12)</Select.Option>
              </Select>
            </Form.Item>
          )}

          {/* Month Selection */}
          {periodType === "month" && (
            <Form.Item
              name="month"
              label={t("campaigns.selectMonth")}
              rules={[{ required: true, message: t("campaigns.selectPeriodRequired") }]}
            >
              <DatePicker
                picker="month"
                style={{ width: "100%" }}
                placeholder={t("campaigns.selectMonthPlaceholder")}
                format="YYYY-MM"
              />
            </Form.Item>
          )}

          {/* Custom Date Range */}
          {periodType === "custom" && (
            <>
              <Form.Item
                name="start_date"
                label={t("campaigns.dateFrom")}
                rules={[{ required: true, message: t("campaigns.selectDatesRequired") }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={t("campaigns.selectDateFrom")}
                />
              </Form.Item>
              <Form.Item
                name="end_date"
                label={t("campaigns.dateTo")}
                rules={[{ required: true, message: t("campaigns.selectDatesRequired") }]}
                dependencies={["start_date"]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder={t("campaigns.selectDateTo")}
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue("start_date");
                    return startDate && current && current < startDate;
                  }}
                />
              </Form.Item>
            </>
          )}

          <CitiesSelector
            required={true}
            readOnly={false}
            mode="multiple"
            label={t("campaigns.cities")}
          />

          <Form.Item
            name="is_active"
            label={t("campaigns.status")}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t("campaigns.active")}
              unCheckedChildren={t("campaigns.inactive")}
            />
          </Form.Item>

          <Form.Item
            name="is_recurring"
            label={t("campaigns.isRecurring")}
            valuePropName="checked"
            tooltip={t("campaigns.isRecurringTooltip")}
          >
            <Switch
              checkedChildren={t("campaigns.recurring")}
              unCheckedChildren={t("campaigns.oneTime")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CampaignAddForm;
